// src/pages/Projects.js

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Globe, FileSearch, LineChart, Activity,
  ArrowLeft, ArrowUpRight, X, ChevronRight, Sparkles, Loader2
} from 'lucide-react';
import { statusFilters } from '../data/studiosData';
import { useStudios } from '../hooks/useStudios';
import SEO from '../components/SEO';
import './Projects.css';

// Icon mapping
const iconMap = {
  Globe,
  FileSearch,
  LineChart,
  Activity
};

// Animation variants
const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
  }
};

const modalVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.15 } }
};

const modalContentVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: { duration: 0.2 }
  }
};

const Projects = () => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);

  // Fetch studios from Contentful
  const { studios, loading, error } = useStudios();

  // Filter studios based on status
  const filteredStudios = studios.filter(studio => {
    if (statusFilter === 'all') return true;
    return studio.status === statusFilter;
  });

  // Get icon component
  const getIcon = (iconName, size = 24) => {
    const IconComponent = iconMap[iconName];
    return IconComponent ? <IconComponent size={size} strokeWidth={1.5} /> : null;
  };

  // Close modal
  const closeModal = () => setSelectedProject(null);

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (selectedProject) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedProject]);

  // Loading state
  if (loading) {
    return (
      <div className="projects-page">
        <div className="projects-page__bg">
          <div className="projects-page__gradient" />
          <div className="projects-page__grid" />
        </div>
        <div className="projects-page__loading">
          <Loader2 size={32} className="projects-page__spinner" />
          <span>Loading studios...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="projects-page">
        <div className="projects-page__bg">
          <div className="projects-page__gradient" />
          <div className="projects-page__grid" />
        </div>
        <div className="projects-page__error">
          <span>Unable to load studios</span>
          <Link to="/" className="projects-page__back-link">
            <ArrowLeft size={18} />
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="projects-page">
      {/* SEO */}
      <SEO
        title="Projects"
        description="Explore Rosalytics product studios: AI-powered solutions for supply chain, manufacturing, and enterprise operations."
        canonical="/projects"
      />

      {/* Background */}
      <div className="projects-page__bg">
        <div className="projects-page__gradient" />
        <div className="projects-page__grid" />
        {/* Floating Particles */}
        <div className="projects-page__particles">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className={`projects-page__particle projects-page__particle--${['violet', 'cyan', 'coral'][i % 3]}`}
              style={{
                left: `${Math.random() * 100}%`,
                animationDuration: `${12 + Math.random() * 8}s`,
                animationDelay: `${Math.random() * 10}s`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Header */}
      <header className="projects-page__header">
        <div className="projects-page__header-content">
          {/* Top row with back link and badge */}
          <div className="projects-page__header-top">
            <Link to="/" className="projects-page__back-link">
              <ArrowLeft size={18} />
              Back to Home
            </Link>
            <span className="projects-page__badge">
              <Sparkles size={14} />
              Product Portfolio
            </span>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="projects-page__title">
              Our <span className="projects-page__title-gradient">Studios</span>
            </h1>
            <p className="projects-page__subtitle">
              Explore our suite of industrial AI products. Each studio is designed
              to solve specific operational challenges with cutting-edge technology.
            </p>
          </motion.div>

          {/* Filters - wrapped for sticky on mobile */}
          <div className="projects-page__filters-wrapper">
            <motion.div
              className="projects-page__filters"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              {statusFilters.map(filter => (
                <button
                  key={filter.id}
                  className={`projects-page__filter-pill ${
                    statusFilter === filter.id ? 'projects-page__filter-pill--active' : ''
                  }`}
                  onClick={() => setStatusFilter(filter.id)}
                >
                  {filter.id === 'live' && <span className="projects-page__filter-dot projects-page__filter-dot--live" />}
                  {filter.id === 'in-lab' && <span className="projects-page__filter-dot projects-page__filter-dot--lab" />}
                  {filter.id === 'coming-soon' && <span className="projects-page__filter-dot projects-page__filter-dot--soon" />}
                  {filter.label}
                </button>
              ))}
            </motion.div>
          </div>
        </div>
      </header>

      {/* Projects Grid */}
      <main className="projects-page__main">
        <motion.div
          className="projects-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <AnimatePresence mode="popLayout">
            {filteredStudios.map((studio) => (
              <motion.div
                key={studio.id}
                className="project-card"
                style={{ '--accent': studio.accent }}
                variants={cardVariants}
                layout
                onClick={() => setSelectedProject(studio)}
              >
                {/* Preview */}
                <div className="project-card__preview">
                  {studio.thumbnail ? (
                    <img
                      src={studio.thumbnail}
                      alt={`${studio.name} preview`}
                      className="project-card__thumbnail"
                    />
                  ) : (
                    <div className="project-card__icon-placeholder">
                      {getIcon(studio.icon, 40)}
                    </div>
                  )}
                  <div className="project-card__overlay">
                    <span className="project-card__view-btn">
                      View Details
                      <ArrowUpRight size={16} />
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="project-card__content">
                  <div className="project-card__header">
                    <span className={`status-chip status-chip--${studio.status}`}>
                      {studio.statusLabel}
                    </span>
                    <span className="project-card__domain">{studio.domainLabel}</span>
                  </div>

                  <h3 className="project-card__title">{studio.name}</h3>
                  <p className="project-card__tagline">{studio.tagline}</p>

                  {/* Tech Stack Preview */}
                  {studio.techStack && (
                    <div className="project-card__tech-preview">
                      {studio.techStack.slice(0, 3).map((tech, i) => (
                        <span key={i} className="project-card__tech-tag">{tech}</span>
                      ))}
                      {studio.techStack.length > 3 && (
                        <span className="project-card__tech-more">
                          +{studio.techStack.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {filteredStudios.length === 0 && (
          <div className="projects-page__empty">
            <p>No projects found with the selected filter.</p>
          </div>
        )}
      </main>

      {/* Project Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            className="project-modal"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={handleBackdropClick}
          >
            <motion.div
              className="project-modal__content"
              style={{ '--accent': selectedProject.accent }}
              variants={modalContentVariants}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="project-modal__close" onClick={closeModal}>
                <X size={20} />
              </button>

              <div className="project-modal__layout">
                {/* Left: Image */}
                <div className="project-modal__image-side">
                  <div className="project-modal__preview">
                    {selectedProject.thumbnail ? (
                      <img
                        src={selectedProject.thumbnail}
                        alt={`${selectedProject.name} preview`}
                        className="project-modal__thumbnail"
                      />
                    ) : (
                      <div className="project-modal__icon-placeholder">
                        {getIcon(selectedProject.icon, 64)}
                        <span className="project-modal__placeholder-label">
                          {selectedProject.name}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: Details */}
                <div className="project-modal__details-side">
                  <div className="project-modal__header">
                    <span className={`status-chip status-chip--${selectedProject.status}`}>
                      {selectedProject.statusLabel}
                    </span>
                    <span className="project-modal__domain">{selectedProject.domainLabel}</span>
                  </div>

                  <h2 className="project-modal__title">{selectedProject.name}</h2>
                  <p className="project-modal__tagline">{selectedProject.tagline}</p>
                  <p className="project-modal__description">{selectedProject.shortDescription}</p>

                  {/* Bullets */}
                  <ul className="project-modal__bullets">
                    {selectedProject.bullets.map((bullet, i) => (
                      <li key={i}>
                        <ChevronRight size={14} style={{ color: selectedProject.accent }} />
                        {bullet}
                      </li>
                    ))}
                  </ul>

                  {/* Tech Stack */}
                  {selectedProject.techStack && (
                    <div className="project-modal__tech-stack">
                      <span className="project-modal__tech-label">Tech Stack</span>
                      <div className="project-modal__tech-tags">
                        {selectedProject.techStack.map((tech, i) => (
                          <span key={i} className="project-modal__tech-tag">{tech}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="project-modal__actions">
                    {selectedProject.demoUrl ? (
                      <a
                        href={selectedProject.demoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-modal__btn project-modal__btn--primary"
                        style={{ background: selectedProject.accent }}
                      >
                        View Live Demo
                        <ArrowUpRight size={16} />
                      </a>
                    ) : (
                      <button
                        className="project-modal__btn project-modal__btn--secondary"
                        disabled
                      >
                        {selectedProject.status === 'coming-soon' ? 'Coming Soon' : 'In Development'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Projects;
